import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Edit, 
  ArrowBack, 
  Print, 
  Email, 
  Phone, 
  Home, 
  Person, 
  PhotoCamera, 
  MoreVert,
  CloudUpload,
  Delete as DeleteIcon 
} from '@mui/icons-material';
import apiService from '../services/api';
import { format } from 'date-fns';
import CheckInHistory from '../components/CheckInHistory/CheckInHistory';
import MemberPaymentHistory from '../components/MemberPaymentHistory/MemberPaymentHistory';
import ContractPrintDialog from '../components/ContractPrintDialog';

interface Member {
  _id: string;
  accountReference: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string | { mobile: string; work: string };
  dateOfBirth: string;
  idNumber: string; // Add idNumber field
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
  membershipType: string;
  membershipStatus: string;
  startDate: string;
  endDate: string;
  paymentStatus: string;
  notes: string;
  age?: number;
  daysRemaining?: number;
  tags?: string[];
  isActive: boolean;
  profileImage?: string | null;
}

const MemberDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [photoMenuAnchor, setPhotoMenuAnchor] = useState<null | HTMLElement>(null);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [contractPrintOpen, setContractPrintOpen] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Use AbortController for cleanup
    const controller = new AbortController();
    
    const fetchMember = async () => {
      if (!id) {
        console.error('No member ID provided');
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching member details for ID:', id);
        const response = await apiService.getMember(id, controller.signal);
        console.log('Member data received:', response);
        if (response) {
          setMember(response);
        } else {
          console.error('No member data returned from API');
          setNotification({
            message: 'No member data found',
            severity: 'error'
          });
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching member:', error);
          console.error('Error status:', error.response?.status);
          console.error('Error message:', error.response?.data?.message);
          
          let errorMessage = 'Error loading member details';
          if (error.response?.status === 404) {
            errorMessage = 'Member not found';
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          
          setNotification({
            message: errorMessage,
            severity: 'error'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMember();

    return () => {
      controller.abort();
    };
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setPhotoMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setPhotoMenuAnchor(null);
  };

  const handleDialogOpen = () => {
    setPhotoDialogOpen(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setPhotoDialogOpen(false);
  };

  const handleSnackbarClose = () => {
    setNotification(null);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !member) return;

    const file = files[0];
    
    // Frontend validation
    try {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setNotification({ 
          message: 'Invalid file type. Please select a JPEG, PNG, WebP, or GIF image.', 
          severity: 'error' 
        });
        return;
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setNotification({ 
          message: 'File too large. Maximum size is 10MB.', 
          severity: 'error' 
        });
        return;
      }

      // Validate minimum file size (10KB to ensure it's not corrupted)
      const minSize = 10 * 1024; // 10KB
      if (file.size < minSize) {
        setNotification({ 
          message: 'File too small. Please select a valid image file.', 
          severity: 'error' 
        });
        return;
      }

      // Validate filename
      const filename = file.name;
      if (filename.length > 255) {
        setNotification({ 
          message: 'Filename too long. Please rename the file.', 
          severity: 'error' 
        });
        return;
      }

      // Check if file has valid extension
      const validExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
      const fileExtension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
      if (!validExtensions.includes(fileExtension)) {
        setNotification({ 
          message: 'Invalid file extension. Please select a valid image file.', 
          severity: 'error' 
        });
        return;
      }

      // Optional: Validate image dimensions using FileReader and Image
      await validateImageDimensions(file);

      // All validations passed, proceed with upload
      setPhotoLoading(true);
      setNotification({ 
        message: 'Uploading and processing photo...', 
        severity: 'info' 
      });

      const response = await apiService.uploadMemberPhoto(member._id, file);
      
      setMember(prev => prev ? { ...prev, profileImage: response.profileImage } : null);
      setNotification({ 
        message: `Photo uploaded successfully! ${response.metadata ? `Processed as ${response.metadata.processedFormat.toUpperCase()} (${response.metadata.dimensions})` : ''}`, 
        severity: 'success' 
      });

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error: any) {
      console.error('Error uploading photo:', error);
      setNotification({ 
        message: error.response?.data?.message || 'Failed to upload photo. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setPhotoLoading(false);
    }
  };

  // Helper function to validate image dimensions
  const validateImageDimensions = (file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        
        // Check minimum dimensions
        if (img.width < 100 || img.height < 100) {
          reject(new Error('Image too small. Minimum size is 100x100 pixels.'));
          return;
        }

        // Check maximum dimensions
        if (img.width > 4000 || img.height > 4000) {
          reject(new Error('Image too large. Maximum size is 4000x4000 pixels.'));
          return;
        }

        resolve();
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Unable to load image. Please select a valid image file.'));
      };

      img.src = url;
    });
  };

  const handleRequestPhoto = async () => {
    if (!member) return;
    
    try {
      setPhotoLoading(true);
      await apiService.requestMemberPhoto(member._id);
      setNotification({ 
        message: 'Photo request sent to tablet. Please wait for the member to accept.', 
        severity: 'info' 
      });
      handleMenuClose();
      setPhotoDialogOpen(false);
    } catch (error: any) {
      setNotification({ 
        message: error.response?.data?.message || 'Failed to request photo', 
        severity: 'error' 
      });
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!member) return;
    
    try {
      setPhotoLoading(true);
      await apiService.deleteMemberPhoto(member._id);
      setMember({ ...member, profileImage: null });
      setNotification({ message: 'Photo deleted successfully!', severity: 'success' });
      handleMenuClose();
      setPhotoDialogOpen(false);
    } catch (error: any) {
      console.error('Error deleting photo:', error);
      setNotification({ 
        message: error.response?.data?.message || 'Failed to delete photo', 
        severity: 'error' 
      });
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleContractPrint = async () => {
    if (!member) return;
    
    setContractLoading(true);
    setContractPrintOpen(true);
    
    try {
      const response = await apiService.get(`/checkins/contract-print/${member.idNumber}`);
      setContractData(response as any);
    } catch (error) {
      console.error('Error fetching contract data:', error);
      setContractData({
        success: false,
        message: 'Failed to load contract data'
      } as any);
    } finally {
      setContractLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'suspended':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'current':
        return 'success';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMMM dd, yyyy');
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const getPhoneDisplay = (phone: string | { mobile: string; work: string }) => {
    if (typeof phone === 'string') return phone;
    return phone?.mobile || 'N/A';
  };

  // Helper function to construct and validate image URLs
  const getImageUrl = (profileImage: string | null | undefined, bustCache = false) => {
    if (!profileImage) return undefined;
    
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    
    // Ensure profileImage starts with a slash
    const imagePath = profileImage.startsWith('/') ? profileImage : `/${profileImage}`;
    
    // Extract the filename and properly encode it
    const pathParts = imagePath.split('/');
    const filename = pathParts[pathParts.length - 1];
    const encodedFilename = encodeURIComponent(filename);
    const pathWithoutFilename = pathParts.slice(0, -1).join('/');
    
    let imageUrl = `${baseUrl}${pathWithoutFilename}/${encodedFilename}`;
    
    // Add cache busting if requested
    if (bustCache) {
      imageUrl += `?t=${Date.now()}`;
    }
    
    console.log('Constructing image URL:', {
      profileImage,
      imagePath,
      filename,
      encodedFilename,
      pathWithoutFilename,
      imageUrl,
      bustCache
    });
    
    return imageUrl;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!member) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Member not found. Member ID: {id}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The member with ID "{id}" could not be found. This could be because:
        </Typography>
        <ul>
          <li>The member ID is invalid</li>
          <li>The member has been deleted</li>
          <li>There was an error loading the member data</li>
        </ul>
        <Button 
          variant="contained" 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/members')}
          sx={{ mt: 2 }}
        >
          Back to Members
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center">
          <Button 
            variant="outlined" 
            startIcon={<ArrowBack />} 
            onClick={() => navigate('/members')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">{member.fullName || `${member.firstName} ${member.lastName}`}</Typography>
        </Box>
        <Box>
          {member && member._id && (
            <>
              <Button 
                variant="outlined" 
                startIcon={<Print />} 
                sx={{ mr: 1 }}
                onClick={() => {
                  const url = `/members/${member._id}/print-contract`;
                  console.log('Print Contract clicked. Member ID:', member._id);
                  console.log('Navigating to:', url);
                  alert(`Navigating to: ${url}`);
                  try {
                    navigate(url);
                  } catch (error) {
                    console.error('Navigation error:', error);
                    alert('Error navigating to print contract page: ' + error);
                  }
                }}
              >
                Print Contract
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<Print />} 
                sx={{ mr: 2 }}
                onClick={handleContractPrint}
              >
                Usage Report
              </Button>
            </>
          )}
          <Button 
            variant="contained" 
            startIcon={<Edit />} 
            onClick={() => navigate(`/members/${member._id}/edit`)}
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar 
                    src={getImageUrl(member.profileImage)}
                    sx={{ width: 100, height: 100, mb: 2 }}
                    onError={(e) => {
                      console.error('Avatar image failed to load');
                      console.error('Image URL:', getImageUrl(member.profileImage));
                      console.error('Error details:', e);
                      // Hide the broken image by clearing the src
                      (e.target as HTMLImageElement).src = '';
                    }}
                  >
                    {!member.profileImage && `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`}
                  </Avatar>
                  <IconButton
                    onClick={handleMenuClick}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: -8,
                      bgcolor: 'primary.main',
                      color: 'white',
                      width: 32,
                      height: 32,
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="h5" align="center">{member.fullName || `${member.firstName} ${member.lastName}`}</Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Account Number: {member.accountReference || 'N/A'}
                </Typography>
                <Box mt={1}>
                  <Chip 
                    label={member.membershipStatus} 
                    color={getStatusColor(member.membershipStatus) as any} 
                    sx={{ mr: 1 }}
                  />
                  <Chip 
                    label={member.paymentStatus} 
                    color={getPaymentStatusColor(member.paymentStatus) as any} 
                  />
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <List disablePadding>
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Membership Type"
                    secondary={member.membershipType}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Joined"
                    secondary={formatDate(member.startDate)}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Expires"
                    secondary={formatDate(member.endDate)}
                  />
                </ListItem>
                {member.daysRemaining !== undefined && (
                  <ListItem disableGutters>
                    <ListItemText 
                      primary="Days Remaining"
                      secondary={member.daysRemaining > 0 ? `${member.daysRemaining} days` : 'Expired'}
                    />
                  </ListItem>
                )}
                {member.age !== undefined && (
                  <ListItem disableGutters>
                    <ListItemText 
                      primary="Age"
                      secondary={`${member.age} years`}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {member.tags && member.tags.length > 0 && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Tags</Typography>
                <Box>
                  {member.tags.map((tag, index) => (
                    <Chip key={index} label={tag} sx={{ m: 0.5 }} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Email sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">{member.email}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Phone sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">{getPhoneDisplay(member.phone)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="flex-start" mb={1}>
                    <Home sx={{ mr: 1, mt: 0.5, color: 'primary.main' }} />
                    <Typography variant="body1">
                      {member.address?.street}, {member.address?.city}, {member.address?.state} {member.address?.zipCode}, {member.address?.country}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Emergency Contact
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Person sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">
                      {member.emergencyContact?.name} ({member.emergencyContact?.relationship})
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Phone sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">{member.emergencyContact?.phone}</Typography>
                  </Box>
                </Grid>
              </Grid>

              {member.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body1">{member.notes}</Typography>
                </>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                <Tab label="Check-in History" />
                <Tab label="Invoices" />
                <Tab label="Payments" />
                <Tab label="Contracts" />
              </Tabs>

              <Box sx={{ mt: 2 }}>
                {tabValue === 0 && <CheckInHistory memberId={member._id} />}
                {tabValue === 1 && <Box p={2}><Typography variant="body2" color="text.secondary">Invoices functionality coming soon</Typography></Box>}
                {tabValue === 2 && <MemberPaymentHistory memberId={member._id} />}
                {tabValue === 3 && <Box p={2}><Typography variant="body2" color="text.secondary">Contracts functionality coming soon</Typography></Box>}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Photos
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Photo Management Options
          </Typography>
          
          <Grid container spacing={2} mb={2}>
            {/* Option 1: Upload from Computer */}
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                <CloudUpload sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Upload from Computer
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload a high-quality photo (JPEG, PNG, WebP, GIF). Max 10MB, Min 100x100px
                </Typography>
                <input
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  id="upload-photo"
                  type="file"
                  hidden
                  onChange={handlePhotoUpload}
                  ref={fileInputRef}
                />
                <label htmlFor="upload-photo">
                  <Button 
                    variant="contained" 
                    component="span" 
                    fullWidth
                    disabled={photoLoading}
                    startIcon={photoLoading ? <CircularProgress size={20} /> : <CloudUpload />}
                  >
                    {photoLoading ? 'Processing...' : 'Choose File'}
                  </Button>
                </label>
              </Card>
            </Grid>

            {/* Option 2: Request from Tablet */}
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                <PhotoCamera sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Request from Tablet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Send request to tablet for member to take photo
                </Typography>
                <Button 
                  variant="contained" 
                  color="secondary"
                  fullWidth
                  onClick={handleRequestPhoto}
                  disabled={photoLoading}
                  startIcon={photoLoading ? <CircularProgress size={20} /> : <PhotoCamera />}
                >
                  {photoLoading ? 'Sending...' : 'Send Request'}
                </Button>
              </Card>
            </Grid>

            {/* Option 3: Manage Existing Photo */}
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                <Person sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Manage Photo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {member.profileImage ? 'View, replace, or delete current photo' : 'No photo available'}
                </Typography>
                {member.profileImage ? (
                  <Button 
                    variant="outlined" 
                    color="warning"
                    fullWidth
                    onClick={handleMenuClick}
                  >
                    Manage Photo
                  </Button>
                ) : (
                  <Button 
                    variant="outlined" 
                    disabled
                    fullWidth
                  >
                    No Photo Set
                  </Button>
                )}
              </Card>
            </Grid>
          </Grid>

          {/* Current Photo Display */}
          {member.profileImage && (
            <Box display="flex" justifyContent="center" mb={2}>
              <Box textAlign="center">
                <Typography variant="h6" gutterBottom>
                  Current Photo
                </Typography>
                <Typography variant="caption" display="block" gutterBottom sx={{ wordBreak: 'break-all' }}>
                  URL: {getImageUrl(member.profileImage)}
                </Typography>
                <Avatar 
                  src={getImageUrl(member.profileImage)} 
                  alt={member.fullName} 
                  sx={{ width: 120, height: 120, mx: 'auto', mb: 1 }}
                  onError={(e) => {
                    console.error('Large avatar image failed to load:', e);
                    console.error('Large avatar image URL:', getImageUrl(member.profileImage));
                  }}
                />
                {/* Test with regular img tag */}
                <Box mt={2}>
                  <Typography variant="caption" display="block" gutterBottom>
                    Test with img tag:
                  </Typography>
                  <Box
                    component="img"
                    src={getImageUrl(member.profileImage, true)} 
                    alt="Test" 
                    sx={{ width: 100, height: 100, objectFit: 'cover', border: '1px solid #ccc' }}
                    onError={(e) => {
                      console.error('Regular img tag failed to load:', e);
                    }}
                    onLoad={() => {
                      console.log('Regular img tag loaded successfully!');
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
          {/* Photo Menu for existing photos */}
          <Menu
            anchorEl={photoMenuAnchor}
            open={Boolean(photoMenuAnchor)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleRequestPhoto}>
              <ListItemText primary="Request New Photo from Tablet" />
              <PhotoCamera sx={{ color: 'primary.main' }} />
            </MenuItem>
            <MenuItem onClick={handleDialogOpen}>
              <ListItemText primary="Delete Photo" />
              <DeleteIcon sx={{ color: 'error.main' }} />
            </MenuItem>
          </Menu>

          <Dialog
            open={photoDialogOpen}
            onClose={handleDialogClose}
          >
            <DialogTitle>Delete Photo</DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                Are you sure you want to delete this photo?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} color="primary" disabled={photoLoading}>
                Cancel
              </Button>
              <Button 
                onClick={handleDeletePhoto} 
                color="error"
                disabled={photoLoading}
                startIcon={photoLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
              >
                {photoLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar 
            open={notification !== null} 
            autoHideDuration={6000} 
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleSnackbarClose} severity={notification?.severity || 'info'} sx={{ width: '100%' }}>
              {notification?.message || ''}
            </Alert>
          </Snackbar>

          {/* Contract Print Dialog */}
          <ContractPrintDialog 
            open={contractPrintOpen} 
            onClose={() => setContractPrintOpen(false)} 
            contractData={contractData}
            loading={contractLoading}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default MemberDetail;